/* import __COLOCATED_TEMPLATE__ from './teammate-content.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import type PartGroup from 'embercom/objects/inbox/conversation-stream/part-group';
import { PartGroupCategory } from 'embercom/objects/inbox/conversation-stream/part-group';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type AdminComment from 'embercom/objects/inbox/renderable/admin-comment';
import type AdminNote from 'embercom/objects/inbox/renderable/admin-note';
import type Conversation from 'embercom/objects/inbox/conversation';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import {
  isTranslatingPartGroup,
  translatedPartExists,
  hasTranslationStatusInPartGroup,
  getAllTranslationLanguageCodesFromPartGroup,
  getOriginalLanguageCodeFromPartGroup,
  getLanguageLabelFromCode,
} from '../helpers/translations';
import type IntlService from 'ember-intl/services/intl';
import {
  nextInCycle,
  normalizeLocale,
  getDefaultWorkspaceLocale,
} from '../helpers/language-selection';
import { computeTranslationUi } from '../helpers/translation-ui';

export interface TeammateRenderablePart extends RenderablePart {
  renderableData: AdminComment | AdminNote;
}

interface TeammatePartGroup extends PartGroup {
  parts: Array<TeammateRenderablePart>;
}

interface Args {
  partGroup: TeammatePartGroup;
  conversation?: Conversation;
  isReadOnly: boolean;
}

interface Signature {
  Args: Args;
}

export default class ConversationStreamTeammateContentComponent extends Component<Signature> {
  categories = PartGroupCategory;
  renderableType = RenderableType;
  @service declare session: Session;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;
  @service declare intl: IntlService;

  @tracked selectedLangCode?: string;

  get teammate(): AdminSummary {
    return this.args.partGroup.parts[0].renderableData.adminSummary;
  }

  get lastPartSeenByUser() {
    let { conversation } = this.args;

    return (
      conversation?.lastSeenByUserAt &&
      conversation?.lastSeenByUserAt >= this.lastPartInGroup.createdAt
    );
  }

  get lastPartInGroup() {
    let { parts } = this.args.partGroup;
    return parts[parts.length - 1];
  }

  get isOperator() {
    let lastPart = this.lastPartInGroup;
    return lastPart.renderableType === RenderableType.TicketStateUpdated;
  }

  get autoTranslationEnabled() {
    return this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
      this.args.conversation,
    );
  }

  get availableTranslationCodes(): string[] {
    return getAllTranslationLanguageCodesFromPartGroup(this.args.partGroup);
  }

  get originalLanguageCode(): string | undefined {
    return getOriginalLanguageCodeFromPartGroup(this.args.partGroup);
  }

  get defaultLanguageCode(): string | undefined {
    return getDefaultWorkspaceLocale(this.session);
  }

  private get translationUi() {
    return computeTranslationUi({
      session: this.session,
      available: this.availableTranslationCodes,
      preferred: this.session?.teammate?.teammatePreferredLocale,
      defaultLang: this.defaultLanguageCode,
      original: this.originalLanguageCode,
      selectedLangCode: this.selectedLangCode,
    });
  }

  get cycleOrderCodes(): string[] {
    return this.translationUi.cycleOrderCodes;
  }

  get currentSelectedCode(): string | undefined {
    return this.translationUi.currentSelectedCode;
  }

  get selectedLanguageLabel(): string {
    let code = this.currentSelectedCode;
    if (!code) {
      return '';
    }
    return getLanguageLabelFromCode(code, this.intl.primaryLocale) || '';
  }

  get translatedLanguage(): string {
    if (this.conversationLanguageCode) {
      return this.conversationLanguageLabel;
    }
    let original = normalizeLocale(this.originalLanguageCode);
    let code =
      this.cycleOrderCodes.find((c) => c !== original) ||
      this.defaultLanguageCode ||
      this.session.workspace?.locale;
    return code ? getLanguageLabelFromCode(code, this.intl.primaryLocale) || '' : '';
  }

  get sentLanguageLabel(): string {
    let code = this.originalLanguageCode;
    if (!code) {
      return '';
    }
    return getLanguageLabelFromCode(code, this.intl.primaryLocale) || '';
  }

  get conversationLanguageCode(): string | undefined {
    let raw = this.args.conversation?.getAttributeForDescriptor('language')?.value as
      | string
      | undefined;
    return normalizeLocale(typeof raw === 'string' ? raw : undefined);
  }

  get conversationLanguageLabel(): string {
    let code = this.conversationLanguageCode;
    if (!code) {
      return '';
    }
    return getLanguageLabelFromCode(code, this.intl.primaryLocale) || '';
  }

  get canShowEditedState(): boolean {
    return !!this.session.workspace?.isFeatureEnabled('spg-inbox-edit-notes');
  }

  get showOriginalMessage(): boolean {
    if (!this.translatedPartExists) {
      return true;
    }
    if (this.args.isReadOnly) {
      return (
        normalizeLocale(this.currentSelectedCode) === normalizeLocale(this.originalLanguageCode)
      );
    }
    return normalizeLocale(this.currentSelectedCode) === normalizeLocale(this.originalLanguageCode);
  }

  get showTranslatedMessage() {
    return !this.showOriginalMessage;
  }

  get translatedPartExists() {
    return translatedPartExists(this.args.partGroup);
  }

  get isTranslationForSocialChannelsEnabled() {
    return this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
      this.args.conversation,
    );
  }

  get isTranslating() {
    return isTranslatingPartGroup(this.args.partGroup);
  }

  get showTranslationToggle() {
    // Always show if there's a translation status (even if translation failed or was filtered)
    if (hasTranslationStatusInPartGroup(this.args.partGroup)) {
      return true;
    }

    if (this.args.isReadOnly) {
      return this.translatedPartExists;
    }

    return this.autoTranslationEnabled && (this.translatedPartExists || this.isTranslating);
  }

  get selectedTranslationLanguageCode(): string | undefined {
    return this.currentSelectedCode;
  }

  get translatedLanguageCode(): string | undefined {
    if (this.conversationLanguageCode) {
      return this.conversationLanguageCode;
    }
    let original = normalizeLocale(this.originalLanguageCode);
    return (
      this.cycleOrderCodes.find((c) => c !== original) ||
      this.defaultLanguageCode ||
      this.session.workspace?.locale
    );
  }

  get showTranslationQualityFeedback() {
    return this.session.workspace.isFeatureEnabled('translation-quality-feedback');
  }

  get currentTranslationAudience(): 'inbound' | 'outbound' {
    let current = normalizeLocale(this.currentSelectedCode);
    let customerCode = normalizeLocale(this.translatedLanguageCode);
    if (customerCode && current === customerCode) {
      return 'outbound';
    }
    return 'inbound';
  }

  @action toggleTranslation() {
    let order = this.cycleOrderCodes;
    let next = nextInCycle(this.currentSelectedCode, order);
    if (next) {
      this.selectedLangCode = next;
    }
  }

  @action onLinkedTicketClick(part: RenderablePart) {
    if (!part.renderableData.linkedTicket?.id) {
      return;
    }
    this.inboxSidebarService.previewConversationById(part.renderableData.linkedTicket.id, {
      selectedTab: 'activity',
      shouldFocusComposer: true,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::PartGroups::TeammateContent': typeof ConversationStreamTeammateContentComponent;
    'inbox2/conversation-stream/part-groups/teammate-content': typeof ConversationStreamTeammateContentComponent;
  }
}
